// Forgot-password flow, two steps on one page:
//   1. "request" — enter the account email; the server always claims
//      success (no account enumeration) and emails a 6-digit code when
//      the account exists.
//   2. "confirm" — enter the code + a new password; on success we bounce
//      back to /login (the reset revokes all sessions server-side, and
//      TOTP still applies at sign-in).

import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthShell from "../components/AuthShell";
import { HxAlert, HxButton, HxInput } from "../components/hx";
// The template's Field parts, as its register-form.tsx lays them out. Not
// HxField: its label takes no htmlFor, and each field is named by its label.
// Record: docs/testing/ui-migration/086-forgot-password.md.
import { Field, FieldGroup, FieldLabel } from "../components/ui/field";
import { PasswordInput } from "../components/ui/password-input";
import { ApiError, opsFetch } from "../api";

type Step = "request" | "confirm";

export default function ForgotPassword() {
  const [step, setStep] = useState<Step>("request");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const navigate = useNavigate();

  async function submitRequest(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);

    try {
      await opsFetch("/ops/auth/password-reset/request", {
        method: "POST",
        json: { email },
      });
      setNotice(
        "If that email belongs to an ops account, a 6-digit code is on its way. It expires in 10 minutes.",
      );
      setStep("confirm");
    } catch (err) {
      if (err instanceof ApiError && (err.status === 429 || err.code === "rate_limited")) {
        setError("Too many attempts. Try again in a few minutes.");
      } else {
        setError("Something went wrong. Try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitConfirm(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);

    try {
      await opsFetch("/ops/auth/password-reset/confirm", {
        method: "POST",
        json: { email, code: code.trim(), newPassword },
      });
      navigate("/login", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429 || err.code === "rate_limited") {
          setError("Too many attempts. Try again in a few minutes.");
        } else if (err.code === "invalid_code") {
          setError("That code isn't right or has expired. Check the email or request a new code.");
        } else if (err.code === "weak_password") {
          setError(err.message || "That password is too weak.");
        } else if (err.code === "password_breached") {
          setError("That password appears in known breaches. Choose another.");
        } else {
          setError("Something went wrong. Try again.");
        }
      } else {
        setError("Something went wrong. Try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function resendCode() {
    setBusy(true);
    setError(null);
    try {
      await opsFetch("/ops/auth/password-reset/request", {
        method: "POST",
        json: { email },
      });
      setNotice("A fresh code has been sent (if the account exists).");
    } catch {
      setError("Couldn't resend just now. Wait a minute and try again.");
    } finally {
      setBusy(false);
    }
  }

  if (step === "request") {
    return (
      <AuthShell
        title="Forgot your password?"
        subtitle="Enter your ops account email and we'll send a reset code."
        footer={<Link to="/login">Back to sign in</Link>}
      >
        <form onSubmit={submitRequest} className="flex flex-col gap-4">
          <FieldGroup className="gap-4">
            <Field className="gap-1.5">
              <FieldLabel htmlFor="email">Email</FieldLabel>
              <HxInput
                id="email"
                type="email"
                autoComplete="username"
                className="w-full"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </Field>
          </FieldGroup>
          <HxButton variant="primary" type="submit" disabled={busy} className="w-full">
            {busy ? "Sending…" : "Send reset code"}
          </HxButton>
          {error && (
            <HxAlert className="mb-0" aria-live="polite">
              {error}
            </HxAlert>
          )}
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Enter your reset code"
      subtitle={`We sent a 6-digit code to ${email}. Enter it below with your new password.`}
      footer={<Link to="/login">Back to sign in</Link>}
    >
      <form onSubmit={submitConfirm} className="flex flex-col gap-4">
        {notice && (
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {notice}
          </p>
        )}
        <FieldGroup className="gap-4">
          <Field className="gap-1.5">
            <FieldLabel htmlFor="code">Reset code</FieldLabel>
            <HxInput
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              placeholder="123456"
              className="w-full"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              required
              autoFocus
            />
          </Field>
          <Field className="gap-1.5">
            <FieldLabel htmlFor="new-password">New password</FieldLabel>
            <PasswordInput
              id="new-password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
          </Field>
          <Field className="gap-1.5">
            <FieldLabel htmlFor="confirm-password">Confirm new password</FieldLabel>
            <PasswordInput
              id="confirm-password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </Field>
        </FieldGroup>
        <HxButton variant="primary" type="submit" disabled={busy || code.length !== 6} className="w-full">
          {busy ? "Resetting…" : "Reset password"}
        </HxButton>
        <HxButton variant="ghost" className="w-full" onClick={resendCode} disabled={busy}>
          Didn't get it? Resend the code
        </HxButton>
        {error && (
          <HxAlert className="mb-0" aria-live="polite">
            {error}
          </HxAlert>
        )}
      </form>
    </AuthShell>
  );
}
